import React, { useCallback, Fragment, useEffect, useContext, useMemo, MouseEvent } from "react";
import { withRouter } from "react-router";
import cn from "classnames";
import moment from "moment";
import { useParams, useHistory } from "react-router-dom";
import { Anchor } from "components/Anchor";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";

import { getCurrencyCode, getLogRocketSessionURL, postHubSpot } from "utils";
import { DialogModalContext } from "components/dialog-modal";

import { If, IfElse } from "components/logic";
import { LightTooltip } from "components/light-tooltip";
import { CustomTooltipContent } from "components/tooltip-content";
import { getTimeRange } from "components/date-picker/utils";
import CONFIG from "config/config";
import { HEADER_TITLE, HELP_URL } from "pages/constants";

import backIcon from "static/media/back.svg";
import faqIcon from "static/media/faq.svg";
import homeIcon from "static/media/home-icon.svg";
import { EDDMContext } from "components/post-hog-provider";
import { NextButton } from "./components";
import { Badge } from "./components/badge";

import style from "./style.js";
import { useStore } from "../../store";
import { StoreType } from "../../store/types";
import { authorization } from "../../module/auth";
import { CurrentStepIcon } from "../../static/media/current-step";
import { CAMPAIGN_ENUM_TYPES } from "../../shared/constants";

const Header: React.FC<any> = ({
  classes,
  isFlyerBuilderPage = false,
  flyerActions,
  tabs: tabsProp = [],
  activeTabIndex,
  hideBackButton,
  headerActions,
  isUploadPage = false,
  isSubmitted,
  hasError = false,
  isDM = false,
}) => {
  const isClient = authorization.isClient();
  const runDialogModal = useContext(DialogModalContext);
  const { isEddmEnabled } = useContext(EDDMContext);

  let tabs: any[] = [];
  if (isEddmEnabled && tabsProp?.length) {
    // REMOVE CHANNEL STEP IF EDDM FLAG IS ENABLED
    if (isDM) {
      tabs = tabsProp;
    } else {
      tabs = tabsProp.slice(1);
    }
  } else if (!isEddmEnabled) {
    tabs = tabsProp;
  }

  const params = useParams();
  const history = useHistory();

  const handleGoToTab = (href: string) => {
    const link = href.replace(":clientId", params.clientId).replace(":campaignId", params.campaignId).replace("?", "");
    history.push(link);
  };

  const {
    user: { id: userId, clientId, clientName, firstName, lastName, email, accessDenied = true },
    campaign,
    costsCalculationData: { detailedCost },
    country,
    missions,
    city,
    errors: { notExistDesignFile },
    updateCampaign,
  }: StoreType = useStore();

  const {
    campaignName,
    id: campaignId,
    internalStatus,
    channel,
    flyerType,
    campaignFlyerInfo,
    flyerWeight,
    startDate,
    qrCodeLink,
    campaignComments,
    stripeCouponCode,
    campaignDuration,
    quote,
    missionsCount,
    hubspotDealId,
  } = campaign;

  const { subtotal, printing, distribution, totalCost, tax } = detailedCost || {};
  const homeUrl = useMemo(() => `${CONFIG.PLATFORM_BASE_URL}${isClient ? `/dashboard/home` : ""}`, [isClient]);

  const handleFAQClick = () => {
    let url = "";

    if (isFlyerBuilderPage) {
      url = HELP_URL[HEADER_TITLE.FLYER_CANVAS];
    } else if (isSubmitted) {
      url = HELP_URL[HEADER_TITLE.SUBMIT];
    } else {
      if (!tabs[activeTabIndex] || !tabs[activeTabIndex].title) return;

      url = HELP_URL[tabs[activeTabIndex].title];
      if (tabs[activeTabIndex].title === HEADER_TITLE.FLYER_BUILDER && isUploadPage) {
        url = HELP_URL[HEADER_TITLE.UPLOAD_DESIGN];
      }
    }
    url = url || "https://selfserve.oppizi.com/";

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleHomeClick = (e: MouseEvent) => {
    const isCtrlClick = e.ctrlKey || e.metaKey;
    if (isCtrlClick) {
      return;
    } else if (!isCtrlClick && !campaign.isSubmitted) {
      runDialogModal({
        title: "Exit campaign creation?",
        contentText: (
          <Box style={{ padding: "10px 40px", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div />
            <div style={{ textAlign: "center" }}>
              Are you sure you want to exit the campaign creation? Any unsaved progress will be lost.
            </div>
          </Box>
        ),
        ctaLabel: "Exit",
        handleCTAClick: () => {
          window.location.href = homeUrl;
        },
        secondaryButtonLabel: "Cancel",
        hasOptions: true,
      });
    }
  };

  const flyerActionClick = useCallback((item, e) => {
    if ((item?.variant && item.isActive) || item.title === "Home" || item.title === "Save") {
      item.action(e);
    }
  }, []);

  const getTooltipContent = ({ isValidUrl = false, isQrCodeOnCanvas = false }) => {
    if (!isQrCodeOnCanvas) {
      return (
        <CustomTooltipContent
          title="QR code is outside the safe area!"
          content="Please, make sure the QR code is within the safe areas."
        />
      );
    }

    if (!isValidUrl) {
      return <CustomTooltipContent title="URL missing!" content="Please, provide a valid URL address." />;
    }

    return "";
  };

  const renderButtons = (item: any, index: number, isNext: boolean) => {
    if (isNext && (!item.isValidUrl || !item.isQrCodeOnCanvas)) {
      return (
        <LightTooltip
          title={getTooltipContent({
            isValidUrl: item.isValidUrl,
            isQrCodeOnCanvas: item.isQrCodeOnCanvas,
          })}
          placement="bottom"
        >
          <span>
            <NextButton key={`${item.title}-${index}`} item={item} onClick={flyerActionClick} />
          </span>
        </LightTooltip>
      );
    } else {
      return item.href ? (
        <Anchor
          href={item.href}
          onClick={(e: MouseEvent) => {
            const isCtrlClick = e.ctrlKey || e.metaKey;
            if (!isCtrlClick && !campaign.isSubmitted) {
              e.preventDefault();
            }
          }}
        >
          <NextButton key={`${item.title}-${index}`} item={item} onClick={flyerActionClick} />
        </Anchor>
      ) : (
        <NextButton key={`${item.title}-${index}`} item={item} onClick={flyerActionClick} />
      );
    }
  };

  useEffect(() => {
    const postHubspotTimer = setTimeout(async () => {
      if (campaignId && clientId && userId && email && activeTabIndex >= 0 && internalStatus === "DRAFT") {
        const logrocket_sessions = getLogRocketSessionURL({ short: false }) || "";
        const stage_id = activeTabIndex + 1;
        const postHubSpotResponse = await postHubSpot({
          deal_id: hubspotDealId,
          pipeline: "OppiziAds",
          stage_id, // required
          campaign_id: campaignId, // required
          company_id: clientId, // required
          contact_id: userId, //required
          email, // required
          logrocket_sessions,
          first_name: firstName || "",
          last_name: lastName || "",
          country: country.name || "",
          city: city?.name || "",
          company_name: clientName || "",
          campaign_name: campaignName || "",
          channel_type: channel || "",
          flyer_type_chosen: campaignFlyerInfo?.isFlyerUploaded
            ? "upload"
            : campaignFlyerInfo?.isFlyerUploaded === false
            ? "design"
            : "" || "",
          flyer_type: flyerType || "",
          flyer_weight: flyerWeight || "",
          design_file_url: campaignFlyerInfo?.s3Link || "",
          launch_date: moment(startDate).format("YYYY-MM-DD") || "",
          campaign_duration: campaignDuration || "",
          qr_code_url: qrCodeLink || "",
          comments: campaignComments || "",
          coupon_code: stripeCouponCode || "",
          tax: tax || 0,
          total: totalCost || 0,
          invoice_number: quote || "",
          number_of_missions: missions?.length || missionsCount || 0,
          number_of_flyers: printing?.quantity || 0,
          amount: stage_id >= 4 ? totalCost : subtotal || 0,
          sub_total: subtotal || 0,
          currency: getCurrencyCode(country) || "",
          distribution_cost: distribution?.total || 0,
          distribution_unit_cost: distribution?.rate || 0,
          printing_cost: printing?.total || 0,
          printing_unit_cost: printing?.rate || 0,
          hasLocations: !!missions?.length,
          missions: missions?.length
            ? // @ts-ignore
              missions.reduce(
                (
                  acc: string,
                  { value, startDate, time }: { value: string; startDate: string; time: string },
                  index: number
                ): string =>
                  (acc += `${value} - ${moment(startDate).format("YYYY-MM-DD")}${
                    time ? " " + getTimeRange(parseInt(time)) : ""
                  }${index < missions.length - 1 ? ", " : ""}`),
                "" as string
              )
            : "",
        });
        if (postHubSpotResponse) {
          const { deal_id } = postHubSpotResponse;
          updateCampaign({ hubspotDealId: deal_id });
        }
      }
    }, 1000);

    return () => {
      clearTimeout(postHubspotTimer);
    };
  }, [activeTabIndex, missions]);

  const completedStep = tabs.find((item: any) => item.step === campaign.lastActiveStep);

  const disabledStepsByFlyerDesignNotExisting = notExistDesignFile ? ["details", "submit"] : [];

  return (
    <IfElse condition={accessDenied}>
      <Box className={cn(classes.headerWrapper, { [classes.eddmHeaderWrapper]: isDM })} />
      <Box className={cn(classes.headerWrapper, { [classes.eddmHeaderWrapper]: isDM })}>
        <IfElse condition={isFlyerBuilderPage}>
          <Box className={classes.flyerContainer}>
            <div className={classes.firstHeaderItem}>
              <Box className={classes.homeAndBackWrapper}>
                <Anchor
                  href={homeUrl}
                  className={classes.homeBtn}
                  onClick={(e: MouseEvent) => {
                    const isCtrlClick = e.ctrlKey || e.metaKey;
                    if (!isCtrlClick && !campaign.isSubmitted) {
                      e.preventDefault();
                    }
                  }}
                >
                  <img src={homeIcon} onClick={handleHomeClick} />
                </Anchor>
                <Box
                  className={cn(classes.backBtn)}
                  onClick={() => headerActions?.BACK?.action()}
                  // @ts-ignore
                  cypress_id={headerActions?.BACK?.cypressId}
                >
                  <img src={backIcon} />
                  <Typography>Back</Typography>
                </Box>
              </Box>
            </div>
            <div className={classes.middleHeaderItem}>
              <Box className={classes.nameAndBadgeWrapper}>
                <Typography>{campaignName}</Typography>
                <Badge totalClassName={classes.totalCost} />
              </Box>
            </div>
            <div className={classes.lastHeaderItem}>
              <Box style={{ display: "flex", justifyContent: "end", alignItems: "center" }}>
                <Box className={classes.navigationBtnsWrapper}>
                  {flyerActions?.map((item: any, index: number) =>
                    renderButtons(item, index, flyerActions.length - 1 === index)
                  )}
                  <Box onClick={handleFAQClick}>
                    <img src={faqIcon} />
                    <span>Help</span>
                  </Box>
                </Box>
              </Box>
            </div>
          </Box>
          <Box className={classes.container}>
            <Grid container className={classes.gridWrapper}>
              <Grid xs={2} item>
                <If condition={!hideBackButton}>
                  <Box
                    className={classes.backBtn}
                    onClick={() => headerActions?.BACK?.action()}
                    // @ts-ignore
                    cypress_id={headerActions?.BACK?.cypressId}
                  >
                    <img src={backIcon} />
                    <Typography>Back</Typography>
                  </Box>
                </If>
              </Grid>
              <Grid xs={8} item>
                <Box className={classes.tabsWrapper}>
                  {tabs.map((item: any) => {
                    const isActive = activeTabIndex === item.index;
                    const errorDeletedDesign = disabledStepsByFlyerDesignNotExisting.includes(item.step);
                    const isProcessedChannel = channel !== CAMPAIGN_ENUM_TYPES.UNCERTAIN && channel;

                    const checkProcessedChannel = isProcessedChannel
                      ? completedStep?.index + 1 >= item.index
                      : completedStep?.index >= item.index;

                    const canBeActive = !hasError && !errorDeletedDesign && completedStep && checkProcessedChannel;
                    const isLink = canBeActive && !isActive;

                    return (
                      <Fragment key={item.index}>
                        <span
                          onClick={() => {
                            if (isLink) {
                              handleGoToTab(item.href);
                            }
                          }}
                          className={cn(classes.item, {
                            [classes.itemActive]: isActive,
                            [classes.itemCanBeActive]: isLink && canBeActive,
                          })}
                        >
                          {isActive ? (
                            <CurrentStepIcon />
                          ) : completedStep && completedStep.index >= item.index ? (
                            <CheckCircleIcon />
                          ) : (
                            <RadioButtonUncheckedIcon color={isActive || canBeActive ? "inherit" : "disabled"} />
                          )}
                          <Box key={`${item.title}-${item.index}`} className={classes.tabWrapper}>
                            <Typography
                              className={cn(classes.tab, {
                                [classes.activeTab]: isActive || canBeActive,
                                [classes.link]: isLink,
                              })}
                            >
                              {item.title}
                            </Typography>
                          </Box>
                        </span>
                        {item.index < 4 ? (
                          <Box
                            key={`seperator-${item.index}`}
                            className={cn(classes.separator, {
                              [classes.disabled]: !canBeActive,
                            })}
                          />
                        ) : null}
                      </Fragment>
                    );
                  })}
                </Box>
              </Grid>
              <Grid xs={2} item>
                <Box className={classes.navigationBtnsWrapper}>
                  <Anchor
                    href={homeUrl}
                    onClick={(e: MouseEvent) => {
                      const isCtrlClick = e.ctrlKey || e.metaKey;
                      if (!isCtrlClick && !campaign.isSubmitted) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <img src={homeIcon} onClick={handleHomeClick} />
                  </Anchor>

                  <div onClick={handleFAQClick}>
                    <img src={faqIcon} />
                    <span>Help</span>
                  </div>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </IfElse>
      </Box>
    </IfElse>
  );
};

export default withStyles(style as any)(withRouter(Header));
